import {
  Box,
  GridItem,
  SimpleGrid,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { isEqual } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { pxToRem } from "adminComponents/utils/pxToRem";
import { useSessionScene } from "links/lib/contexts/sessionScene";
import {
  AnalyticsEvent,
  ISessionUser,
  IUser,
  SessionGameType,
} from "links/lib/types";
import { Button } from "sessionComponents/atoms/Button";
import { useBreakpoints } from "sessionComponents/contexts/breakpoints";
import { useLobbyAudio } from "sessionComponents/hooks/useLobbyAudio";
import { AvatarCarousel } from "sessionComponents/molecules/AvatarCarousel";
import { ConfirmButton } from "sessionComponents/molecules/ConfirmButton";
import { JoinSessionCard } from "sessionComponents/molecules/JoinSessionCard";
import { LobbyLayout } from "sessionComponents/molecules/LobbyLayout";
import { StudentListModal } from "sessionComponents/molecules/StudentListModal";
import { useSessionAnalytics } from "sharedComponents/contexts/sessionAnalytics";

import { useDetectOrientation } from "../../theme/hooks/useDetectOrientation";

export interface ITeacherLobbyProps {
  outerGame?: SessionGameType;
  onlineStudents: ISessionUser[];
  classroomUsers: IUser[];
  sessionCode: string;
  onStart: () => void;
}

export const TeacherLobby: React.FC<ITeacherLobbyProps> = ({
  outerGame = SessionGameType.TheBigBoard,
  onlineStudents,
  classroomUsers,
  sessionCode,
  onStart,
}) => {
  const {
    match: currentBreakpoints,
    fullViewportHeightMinusHeader,
    size,
  } = useBreakpoints();
  const { isPortrait, isLandscape } = useDetectOrientation();
  const sessionScene = useSessionScene();
  const { t } = useTranslation("session", {
    keyPrefix: "teacherLobby",
    useSuspense: false,
  });
  const { track } = useSessionAnalytics();
  useLobbyAudio();

  const isQuickPlay = outerGame === SessionGameType.QuickPlay;

  const [onlineStudentsState, setOnlineStudentsState] =
    useState(onlineStudents);

  // keep onlineStudents in state to avoid rerendering avatars when we don't need to
  useEffect(() => {
    if (!isEqual(onlineStudents, onlineStudentsState)) {
      setOnlineStudentsState(onlineStudents);
    }
  }, [onlineStudents, onlineStudentsState]);

  const onlineStudentStats = useMemo(() => {
    const guestCount = onlineStudentsState.filter(
      (student) => student.is_guest
    ).length;

    return {
      onlineClassroomStudentsCount: onlineStudentsState.length - guestCount,
      onlineGuestsCount: guestCount,
    };
  }, [onlineStudentsState]);

  const {
    isOpen: isStudentListModalOpen,
    onOpen,
    onClose: onCloseStudentModal,
  } = useDisclosure();

  const fontSize = pxToRem(currentBreakpoints.fontSize);

  let btnColumns = isPortrait ? 1 : 2;
  if (isPortrait && size === "xl") btnColumns = 2;

  const content = (
    <VStack
      w="100%"
      h="100%"
      maxH={fullViewportHeightMinusHeader}
      overflow="hidden"
      position="relative"
    >
      <Box
        w="full"
        flexGrow={2}
        padding={pxToRem(currentBreakpoints.padding)}
        paddingLeft={isPortrait ? undefined : 0}
      >
        <JoinSessionCard
          classroomStudentsJoinedCount={
            onlineStudentStats.onlineClassroomStudentsCount
          }
          guestStudentsJoinedCount={onlineStudentStats.onlineGuestsCount}
          classroomStudentCount={classroomUsers.length}
          sessionCode={sessionCode}
        />
      </Box>

      <SimpleGrid
        w="100%"
        position="relative"
        columns={btnColumns}
        spacing={pxToRem(currentBreakpoints.padding / 2)}
        padding={pxToRem(currentBreakpoints.padding)}
        paddingTop={0}
        paddingLeft={isPortrait ? pxToRem(currentBreakpoints.padding) : 0}
        paddingBottom={pxToRem(currentBreakpoints.padding)}
      >
        <GridItem w="100%">
          <Button
            onClick={() => {
              onOpen();
              track(AnalyticsEvent.Session_Lobby_ToggleStudentList, {
                isOpen: isStudentListModalOpen,
              });
            }}
            fontSize={fontSize}
            borderWidth={currentBreakpoints.borderWidth}
            w="100%"
            height={pxToRem(currentBreakpoints.buttonHeight)}
            variant="buttonOutlined"
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
            }}
          >
            {t("studentsButton")}
          </Button>
        </GridItem>
        <GridItem w="100%">
          <ConfirmButton
            borderWidth={currentBreakpoints.borderWidth}
            borderColor={isQuickPlay ? "primary.white" : undefined}
            confirmedWhenChanged={[sessionScene]}
            handleConfirmClick={onStart}
            fontSize={fontSize}
            w="100%"
            variant="buttonFilled"
            disabled={onlineStudents.length < 1}
            height={pxToRem(currentBreakpoints.buttonHeight)}
          >
            {t("startButton")}
          </ConfirmButton>
        </GridItem>
      </SimpleGrid>

      {isStudentListModalOpen && (
        <StudentListModal
          isOpen={isStudentListModalOpen}
          onClose={onCloseStudentModal}
        />
      )}
    </VStack>
  );

  const avatarStage = useMemo(
    () => <AvatarCarousel students={onlineStudentsState} selfId="0" />,
    [onlineStudentsState]
  );

  return (
    <Box position="relative">
      {!isQuickPlay && (
        <LobbyLayout
          avatarComponent={isLandscape ? avatarStage : null}
          actionContent={content}
          stageProps={{ showCurtainOnly: isPortrait }}
        />
      )}

      {isQuickPlay && (
        <LobbyLayout
          avatarComponent={isLandscape ? avatarStage : null}
          actionContent={content}
          stageProps={{ showCurtainOnly: isPortrait, outerGame }}
        />
      )}
    </Box>
  );
};
